import React, { useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/AuthProvider";
import { authApi } from "../../services/auth";
import { useCart } from "../../contexts/CartProvider";
import { cartSyncGuestToUser } from "../../api/cartApi";
import {
  AlertTriangle,
  ArrowUpRight,
  Eye,
  EyeOff,
  Loader2,
  LockKeyhole,
  Mail,
} from "lucide-react";
import { signInWithPopup } from "firebase/auth";
import { fbAuth, googleProvider } from "../../lib/firebase";

function cn(...x: Array<string | false | undefined | null>) {
  return x.filter(Boolean).join(" ");
}

function isAdminUser(u: any) {
  if (!u) return false;

  if (u.role && String(u.role).toLowerCase() === "admin") return true;
  if (u.isAdmin === true) return true;
  if (u.roleName && String(u.roleName).toLowerCase() === "admin") return true;
  if (u.userType && String(u.userType).toLowerCase() === "admin") return true;

  if (Array.isArray(u.roles) && typeof u.roles[0] !== "object") {
    return u.roles.some((r: any) => String(r).toLowerCase() === "admin");
  }

  if (Array.isArray(u.roles) && typeof u.roles[0] === "object") {
    return u.roles.some((r: any) => {
      const v = r?.code ?? r?.name ?? r?.role ?? "";
      return String(v).toLowerCase() === "admin";
    });
  }

  return false;
}

const RAW_ADMIN_APP_URL =
  process.env.REACT_APP_ADMIN_APP_URL ||
  process.env.REACT_APP_ADMIN_URL ||
  process.env.REACT_APP_ADMIN_FRONTEND_URL ||
  "";

const ADMIN_APP_URL = String(RAW_ADMIN_APP_URL).replace(/\/+$/, "");

function buildAdminRedirectHash(payload: any) {
  const encoded = encodeURIComponent(btoa(JSON.stringify(payload)));
  return `#${encoded}`;
}

function getFrom(location: ReturnType<typeof useLocation>) {
  const stateFrom = (location.state as any)?.from;
  const qsFrom = new URLSearchParams(location.search).get("from");
  return (stateFrom || qsFrom || "").toString();
}

function normalizeAdminPath(path: string) {
  if (!path) return "/dashboard";
  return path.startsWith("/") ? path : `/${path}`;
}

function isAdminRoute(path: string) {
  return path === "/dashboard" || path.startsWith("/dashboard/");
}

function InputLabel(props: {
  children: React.ReactNode;
  right?: React.ReactNode;
}) {
  return (
    <div className="mb-2 flex items-center justify-between gap-3">
      <p className="text-[10px] font-medium uppercase tracking-[0.16em] text-brand-brown/46">
        {props.children}
      </p>
      {props.right}
    </div>
  );
}

function FieldShell(props: {
  icon: React.ReactNode;
  children: React.ReactNode;
  error?: boolean;
  trailing?: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-[18px] border bg-white/76 backdrop-blur-sm transition",
        props.error
          ? "border-red-500/35 focus-within:border-red-500/55"
          : "border-brand-brown/10 hover:border-brand-brown/20 focus-within:border-brand-brown/28",
      )}
    >
      <div className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-brand-brown/38">
        {props.icon}
      </div>

      <div className={cn(props.trailing ? "pr-12" : "")}>{props.children}</div>

      {props.trailing && (
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          {props.trailing}
        </div>
      )}
    </div>
  );
}

type ActionButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "secondary";
};

function ActionButton({
  variant = "primary",
  className,
  children,
  ...props
}: ActionButtonProps) {
  return (
    <button
      {...props}
      className={cn(
        "inline-flex h-11 w-full items-center justify-center gap-2 rounded-[18px] px-4 transition",
        "text-[10px] font-medium uppercase tracking-[0.18em]",
        variant === "primary"
          ? "bg-brand-brown text-brand-ivory shadow-[0_12px_24px_rgba(43,31,26,0.14)] hover:opacity-95 disabled:bg-brand-brown/20 disabled:text-brand-ivory/70"
          : "border border-brand-brown/10 bg-white/80 text-brand-brown/70 hover:border-brand-brown/22 hover:bg-brand-brown/5 hover:text-brand-brown disabled:border-brand-brown/10 disabled:text-brand-brown/30",
        "disabled:cursor-not-allowed",
        className,
      )}
    >
      {children}
    </button>
  );
}

export default function LoginForm() {
  const { t, i18n } = useTranslation();
  const isVN = i18n.language?.startsWith("vi");

  const { setUser, user } = useAuth() as any;
  const { reloadCart } = useCart();

  const navigate = useNavigate();
  const location = useLocation();

  const [usernameOrEmail, setUsernameOrEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(true);

  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  const [errors, setErrors] = useState<{
    usernameOrEmail?: string;
    password?: string;
    form?: string;
  }>({});

  const inputBase =
    "h-12 w-full bg-transparent px-4 text-[15px] text-brand-brown outline-none placeholder:text-brand-brown/28";
  const inputWithIcon = "pl-11";

  const canSubmit = useMemo(() => {
    return usernameOrEmail.trim().length > 0 && password.length > 0 && !loading;
  }, [usernameOrEmail, password, loading]);

  const validate = () => {
    const e: typeof errors = {};
    const value = usernameOrEmail.trim();

    if (!value) e.usernameOrEmail = t("login.errors.usernameOrEmailRequired");
    if (!password) e.password = t("login.errors.passwordRequired");

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  function redirectToAdminApp(path: string, payload: any) {
    if (!ADMIN_APP_URL) return false;

    const normalizedPath = normalizeAdminPath(path);
    const hash = buildAdminRedirectHash(payload);

    window.location.assign(
      `${ADMIN_APP_URL}/auth-redirect${hash}&to=${encodeURIComponent(normalizedPath)}`,
    );
    return true;
  }

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;

    setErrors((prev) => ({ ...prev, form: undefined }));
    if (!validate()) return;

    try {
      setLoading(true);

      const data = await authApi.login(
        usernameOrEmail.trim(),
        password,
        rememberMe,
      );

      if (data?.user) setUser(data.user);

      try {
        await cartSyncGuestToUser();
      } catch (e) {
        console.warn("cartSyncGuestToUser skipped/failed:", e);
      }

      try {
        await reloadCart();
      } catch (e) {
        console.warn("reloadCart failed:", e);
      }

      const currentUser = data?.user ?? user;
      const admin = isAdminUser(currentUser);
      const from = getFrom(location);

      if (from) {
        if (isAdminRoute(from)) {
          if (!admin) {
            navigate("/", { replace: true });
            return;
          }

          const ok = redirectToAdminApp(from, {
            token: data?.token,
            user: currentUser,
          });

          if (!ok) navigate(from, { replace: true });
          return;
        }

        navigate(from, { replace: true });
        return;
      }

      if (admin) {
        const ok = redirectToAdminApp("/dashboard", {
          token: data?.token,
          user: currentUser,
        });

          if (!ok) navigate("/dashboard", { replace: true });
          return;
      }

      navigate("/", { replace: true });
    } catch (err: any) {
      const apiMessage =
        err?.response?.data?.message ||
        err?.response?.data?.Message ||
        err?.response?.data?.error ||
        t("login.errors.invalidCredentials");

      setErrors((prev) => ({ ...prev, form: apiMessage }));
    } finally {
      setLoading(false);
    }
  };

  const onGoogleLogin = async () => {
    if (loading) return;

    setErrors((prev) => ({ ...prev, form: undefined }));

    try {
      setLoading(true);

      const cred = await signInWithPopup(fbAuth, googleProvider);
      const idToken = await cred.user.getIdToken(true);
      const data = await authApi.googleLogin(idToken, rememberMe);

      if (data?.user) setUser(data.user);

      try {
        await cartSyncGuestToUser();
      } catch (e) {
        console.warn("cartSyncGuestToUser skipped/failed:", e);
      }

      try {
        await reloadCart();
      } catch (e) {
        console.warn("reloadCart failed:", e);
      }

      const currentUser = data?.user ?? user;
      const admin = isAdminUser(currentUser);
      const from = getFrom(location);

      if (from) {
        if (isAdminRoute(from)) {
          if (!admin) {
            navigate("/", { replace: true });
            return;
          }

          const ok = redirectToAdminApp(from, {
            token: data?.token,
            user: currentUser,
          });

          if (!ok) navigate(from, { replace: true });
          return;
        }

        navigate(from, { replace: true });
        return;
      }

      if (admin) {
        const ok = redirectToAdminApp("/dashboard", {
          token: data?.token,
          user: currentUser,
        });

        if (!ok) navigate("/dashboard", { replace: true });
        return;
      }

      navigate("/", { replace: true });
    } catch (err: any) {
      const apiMessage =
        err?.response?.data?.message ||
        err?.response?.data?.Message ||
        err?.message ||
        t("login.errors.googleLoginFailed");

      setErrors((prev) => ({ ...prev, form: apiMessage }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="grid gap-4">
        <div>
          <InputLabel>{t("login.usernameOrEmailLabel")}</InputLabel>

          <FieldShell
            icon={<Mail className="h-4 w-4" />}
            error={!!errors.usernameOrEmail}
          >
            <input
              type="text"
              placeholder={t("login.usernameOrEmailPlaceholder")}
              value={usernameOrEmail}
              onChange={(e) => {
                setUsernameOrEmail(e.target.value);
                if (errors.usernameOrEmail) {
                  setErrors((m) => ({ ...m, usernameOrEmail: undefined }));
                }
                if (errors.form) {
                  setErrors((m) => ({ ...m, form: undefined }));
                }
              }}
              autoComplete="username"
              className={cn(inputBase, inputWithIcon)}
            />
          </FieldShell>

          {errors.usernameOrEmail && (
            <p className="mt-1.5 text-[12px] text-red-600/80">
              {errors.usernameOrEmail}
            </p>
          )}
        </div>

        <div>
          <InputLabel
            right={
              <Link
                to="/auth/forgot-password"
                className="text-[10px] font-medium uppercase tracking-[0.14em] text-brand-brown/44 transition hover:text-brand-brown"
              >
                {t("login.forgot")}
              </Link>
            }
          >
            {t("login.passwordLabel")}
          </InputLabel>

          <FieldShell
            icon={<LockKeyhole className="h-4 w-4" />}
            error={!!errors.password}
            trailing={
              <button
                type="button"
                onClick={() => setShowPw((v) => !v)}
                className="grid h-8 w-8 place-items-center rounded-[12px] border border-brand-brown/10 transition hover:border-brand-brown/20 hover:bg-brand-brown/5"
                aria-label={
                  showPw ? t("login.hidePassword") : t("login.showPassword")
                }
              >
                {showPw ? (
                  <EyeOff className="h-4 w-4 text-brand-brown/55" />
                ) : (
                  <Eye className="h-4 w-4 text-brand-brown/55" />
                )}
              </button>
            }
          >
            <input
              type={showPw ? "text" : "password"}
              placeholder="••••••••"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (errors.password) {
                  setErrors((m) => ({ ...m, password: undefined }));
                }
                if (errors.form) {
                  setErrors((m) => ({ ...m, form: undefined }));
                }
              }}
              autoComplete="current-password"
              className={cn(inputBase, inputWithIcon, "pr-12")}
            />
          </FieldShell>

          {errors.password && (
            <p className="mt-1.5 text-[12px] text-red-600/80">
              {errors.password}
            </p>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 rounded-[18px] border border-brand-brown/10 bg-white/54 px-4 py-3">
        <label className="inline-flex items-center gap-2.5 text-[13px] text-brand-brown/56">
          <input
            type="checkbox"
            checked={rememberMe}
            onChange={(e) => setRememberMe(e.target.checked)}
            className="h-4 w-4 rounded border-brand-brown/20"
          />
          <span>{t("login.rememberMe")}</span>
        </label>

        <p className="hidden text-[10px] uppercase tracking-[0.16em] text-brand-brown/34 sm:block">
          {isVN ? "Bảo mật" : "Secure"}
        </p>
      </div>

      {errors.form && (
        <div className="rounded-[18px] border border-red-500/20 bg-red-500/5 px-4 py-3">
          <div className="flex items-start gap-2.5">
            <AlertTriangle className="mt-0.5 h-4 w-4 text-red-600/80" />
            <p className="text-[13px] leading-5 text-red-600/80">{errors.form}</p>
          </div>
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-[1fr_160px]">
        <ActionButton type="submit" variant="primary" disabled={!canSubmit}>
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              {t("login.signingIn")}
            </>
          ) : (
            <>
              {t("login.signIn")}
              <ArrowUpRight className="h-4 w-4" />
            </>
          )}
        </ActionButton>

        <ActionButton
          type="button"
          variant="secondary"
          onClick={onGoogleLogin}
          disabled={loading}
        >
          Google
        </ActionButton>
      </div>

      <div className="rounded-[18px] border border-brand-brown/10 bg-white/46 px-4 py-3 text-center text-[13px] text-brand-brown/56">
        {isVN ? "Chưa có tài khoản?" : "No account yet?"}
        <Link
          to="/auth?tab=register"
          className="ml-2 font-medium text-brand-brown transition hover:text-brand-gold"
        >
          {t("login.createAccountLink")}
        </Link>
      </div>
    </form>
  );
}